import * as React from 'react';
import {
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Ban,
  Check,
  CircleDashed,
  CircleOff,
  Clock,
  Pause,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.20. Every state carries an icon and words, never colour
// alone, and the countdown is monospaced so digits do not jitter as they tick.

export type SlaState =
  | 'met'
  | 'on_track'
  | 'at_risk'
  | 'breached'
  | 'paused'
  | 'escalated'
  | 'stopped'
  | 'not_configured'
  | 'overridden'
  | 'not_started'
  | 'cleared'
  | 'configuration_error';

const STATES: Record<SlaState, { icon: React.ComponentType<{ className?: string }>; className: string }> = {
  met: { icon: Check, className: 'text-success-700' },
  on_track: { icon: Clock, className: 'text-ink' },
  at_risk: { icon: AlertTriangle, className: 'text-warning-700' },
  breached: { icon: AlertOctagon, className: 'text-danger-600 font-medium' },
  paused: { icon: Pause, className: 'text-ink-subtle' },
  escalated: { icon: AlertOctagon, className: 'text-danger-600 font-medium' },
  stopped: { icon: Ban, className: 'text-ink-subtle' },
  not_configured: { icon: CircleDashed, className: 'text-ink-subtle' },
  overridden: { icon: Pencil, className: 'text-brand-700' },
  not_started: { icon: Clock, className: 'text-ink-subtle' },
  cleared: { icon: CircleOff, className: 'text-ink-subtle' },
  configuration_error: { icon: AlertCircle, className: 'text-danger-600 font-medium' },
};

/** At-risk begins at 75% of the target elapsed (§2.20). */
const AT_RISK_RATIO = 0.75;

export function resolveSlaState(
  dueAt: string | Date | null | undefined,
  options: { paused?: boolean; completedAt?: string | Date | null; startedAt?: string | Date | null } = {},
): SlaState | null {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  if (options.completedAt) return new Date(options.completedAt).getTime() <= due ? 'met' : 'breached';
  if (options.paused) return 'paused';

  const now = Date.now();
  if (now > due) return 'breached';
  if (options.startedAt) {
    const started = new Date(options.startedAt).getTime();
    const span = due - started;
    if (span > 0 && (now - started) / span >= AT_RISK_RATIO) return 'at_risk';
  }
  return 'on_track';
}

export function formatDuration(ms: number) {
  const totalMinutes = Math.max(Math.floor(Math.abs(ms) / 60000), 0);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m`;
}

interface SlaIndicatorProps {
  state: SlaState;
  /** Pre-formatted value, e.g. "Due in 3h 20m". */
  value: string;
  label?: string;
  /** Extra context line, e.g. a pause reason or next escalation. */
  hint?: string;
  className?: string;
}

export function SlaIndicator({ state, value, label, hint, className }: SlaIndicatorProps) {
  const { icon: Icon, className: stateClassName } = STATES[state];
  return (
    <div className={cn('min-w-0', className)}>
      <span className={cn('inline-flex items-center gap-1.5 text-sm', stateClassName)}>
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {label && <span className="text-ink-muted">{label}</span>}
        <span className="font-mono">{value}</span>
      </span>
      {hint && <p className="mt-0.5 text-xs text-ink-subtle">{hint}</p>}
    </div>
  );
}
